import { DASH, formatClock, formatNumber, formatRate, humanize, isNum } from "../format";
import type { AcousticResult, SpeakerAcoustics } from "../types";
import { Block, Empty, Stat, Unavailable, type TurnRef } from "./shared";

const SPEAKER_ROWS: Array<{ key: keyof SpeakerAcoustics; label: string; render: (v: number | null) => string }> = [
  { key: "talk_time_s", label: "Talk time", render: formatClock },
  { key: "talk_ratio", label: "Share of talk", render: formatRate },
  { key: "speaking_rate_wpm", label: "Speaking rate (wpm)", render: (v) => formatNumber(v) },
  { key: "avg_pitch_hz", label: "Average pitch (Hz)", render: (v) => formatNumber(v) },
  { key: "pitch_variability_hz", label: "Pitch variability (Hz)", render: (v) => formatNumber(v, 1) },
  { key: "avg_energy_db", label: "Average energy (dB)", render: (v) => formatNumber(v, 1) },
  { key: "energy_variability_db", label: "Energy variability (dB)", render: (v) => formatNumber(v, 1) },
  { key: "jitter_pct", label: "Jitter (%)", render: (v) => formatNumber(v, 1) },
  { key: "shimmer_pct", label: "Shimmer (%)", render: (v) => formatNumber(v, 1) },
];

const ms = (value: number | null | undefined) => (isNum(value) ? `${(value / 1000).toFixed(1)} s` : DASH);

export function AcousticPanel({ result, turnRef }: { result: AcousticResult; turnRef: TurnRef }) {
  const { availability: av } = result;
  const { agent, contact } = result.speakers;

  return (
    <div className="space-y-5">
      {!av.timing || !av.pitch_energy || av.reason ? (
        <Unavailable>
          {[
            !av.timing && "Turn timing could not be measured.",
            !av.pitch_energy && "Pitch and energy could not be measured.",
            av.reason,
          ].filter(Boolean).join(" ")}
        </Unavailable>
      ) : null}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Delivery quality" value={formatNumber(result.quality_score)} hint="out of 100" />
        <Stat label="Call length" value={formatClock(result.duration_s)} />
        <Stat label="Pauses" value={formatNumber(result.pauses?.count)} hint={isNum(result.pauses?.per_minute) ? `${result.pauses!.per_minute!.toFixed(1)} per minute` : undefined} />
        <Stat label="Longest pause" value={ms(result.pauses?.longest_ms)} hint={isNum(result.pauses?.long_count) ? `${result.pauses!.long_count} over 2 s` : undefined} />
      </div>

      <Block title="Per speaker">
        <div className="overflow-x-auto rounded-xl border border-line">
          <table className="w-full text-sm">
            <thead className="bg-soft text-left text-xs text-ink-3">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">Measure</th>
                <th scope="col" className="px-3 py-2 font-medium">Agent</th>
                <th scope="col" className="px-3 py-2 font-medium">Customer</th>
              </tr>
            </thead>
            <tbody>
              {SPEAKER_ROWS.map((row) => (
                <tr key={row.key} className="border-t border-line">
                  <th scope="row" className="px-3 py-1.5 text-left font-normal text-ink-2">{row.label}</th>
                  <td className="px-3 py-1.5 tabular-nums text-ink">{row.render(agent?.[row.key] ?? null)}</td>
                  <td className="px-3 py-1.5 tabular-nums text-ink">{row.render(contact?.[row.key] ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Block>

      <Block title="Quality deductions">
        {result.quality_deductions.length ? (
          <ul className="space-y-1 text-sm text-ink-2">
            {result.quality_deductions.map((d, i) => (
              <li key={i} className="flex justify-between gap-3"><span>{d.reason}</span><span className="tabular-nums text-danger">−{d.points}</span></li>
            ))}
          </ul>
        ) : <Empty>None.</Empty>}
      </Block>

      <div className="grid gap-5 md:grid-cols-2">
        <Block title="Emphasis">
          {result.emphasis.length ? (
            <ul className="space-y-1 text-sm text-ink-2">
              {result.emphasis.map((e, i) => (
                <li key={i}>
                  {e.speaker === "contact" ? "Customer" : "Agent"} at {formatClock(e.at_s)}:{" "}
                  {isNum(e.above_mean_db) ? `${e.above_mean_db.toFixed(1)} dB above their average` : DASH}
                  <span className="ml-1.5 text-xs text-ink-3">{turnRef(e.turn_index)}</span>
                </li>
              ))}
            </ul>
          ) : <Empty>None detected.</Empty>}
        </Block>
        <Block title="Delivery changes">
          {result.delivery_changes.length ? (
            <ul className="space-y-1 text-sm text-ink-2">
              {result.delivery_changes.map((c, i) => (
                <li key={i}>
                  {c.speaker === "contact" ? "Customer" : "Agent"}: {humanize(c.kind)}
                  {isNum(c.delta) ? ` (${c.delta > 0 ? "+" : ""}${c.delta.toFixed(1)})` : ""}
                  <span className="ml-1.5 text-xs text-ink-3">{turnRef(c.turn_index)}</span>
                </li>
              ))}
            </ul>
          ) : <Empty>None detected.</Empty>}
        </Block>
      </div>

      <p className="text-xs text-ink-3">
        Aligned turns: {formatRate(av.aligned_turns)} · Speech model: {av.asr_model || DASH}. Changes are measured against the same speaker's running median.
      </p>
    </div>
  );
}
